import {
  formatUnitFlavorSummary,
  formatCupcakeFrostingDisplay,
  isSingleDozenOrder,
  CUPCAKE_CONFIG_MODES,
  CUPCAKE_CONFIG_MODE_OPTIONS,
  splitUnitsIntoHalves,
} from '../pages/cakeorderform/utils/cupcakeOrderUtils';

function AdminDetailRow({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm sm:text-base text-gray-900 font-medium leading-snug break-words">{value || '—'}</p>
    </div>
  );
}

function AdminUnitCard({ title, subtitle, unit }) {
  if (!unit) return null;
  const flavor = formatUnitFlavorSummary(unit);
  const icing = formatCupcakeFrostingDisplay(unit.icingSelections, unit.icingTintEnabled, unit.icingTintColor);
  const sprinkles = (unit.sugarSprinkleColors || []).join(' + ') || '—';

  return (
    <div className="rounded-lg border border-candy-border overflow-hidden bg-white shadow-sm">
      <div className="bg-[#a62c2b] px-3 py-2 sm:px-4 sm:py-2.5">
        <p className="font-semibold text-white text-sm sm:text-base">{title}</p>
        {subtitle && <p className="text-xs text-red-100 mt-0.5">{subtitle}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4 bg-[#fef9f9]">
        <AdminDetailRow label="Flavor" value={flavor} />
        <AdminDetailRow label="Icing" value={icing} />
        <AdminDetailRow label="Sprinkles" value={sprinkles} />
      </div>
    </div>
  );
}

function AdminByDozenTable({ units }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-candy-border">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="bg-[#a62c2b] text-white text-left">
            <th className="px-3 py-2.5 font-semibold">Unit</th>
            <th className="px-3 py-2.5 font-semibold">Qty</th>
            <th className="px-3 py-2.5 font-semibold">Flavor</th>
            <th className="px-3 py-2.5 font-semibold">Icing</th>
            <th className="px-3 py-2.5 font-semibold">Sprinkles</th>
          </tr>
        </thead>
        <tbody>
          {units.map((unit, idx) => (
            <tr
              key={unit.unitIndex}
              className={idx % 2 === 0 ? 'bg-white' : 'bg-[#fef9f9]'}
            >
              <td className="px-3 py-3 font-semibold text-gray-900 align-top">{unit.label}</td>
              <td className="px-3 py-3 text-gray-700 align-top whitespace-nowrap">
                {unit.size === 1 ? '12' : '6'} cupcakes
              </td>
              <td className="px-3 py-3 text-gray-900 align-top">{formatUnitFlavorSummary(unit)}</td>
              <td className="px-3 py-3 text-gray-900 align-top">
                {formatCupcakeFrostingDisplay(unit.icingSelections, unit.icingTintEnabled, unit.icingTintColor)}
              </td>
              <td className="px-3 py-3 text-gray-900 align-top">
                {(unit.sugarSprinkleColors || []).join(' + ') || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnitBlock({ title, unit }) {
  if (!unit) return null;
  return (
    <div className="rounded-lg border border-candy-border bg-[#fef9f9] p-3 text-sm text-gray-800">
      {title && <p className="font-semibold text-candy-primary mb-1">{title}</p>}
      <p><strong>Flavor:</strong> {formatUnitFlavorSummary(unit)}</p>
      <p><strong>Icing:</strong> {formatCupcakeFrostingDisplay(unit.icingSelections, unit.icingTintEnabled, unit.icingTintColor)}</p>
      <p><strong>Sprinkles:</strong> {(unit.sugarSprinkleColors || []).join(' + ') || '—'}</p>
    </div>
  );
}

export default function CupcakeOrderDetailsDisplay({
  cupcakeDozens = 0,
  cupcakeUnitConfigs = [],
  cupcakeConfigMode = CUPCAKE_CONFIG_MODES.UNIFORM,
  showConfigMode = true,
  variant = 'default',
  className = '',
}) {
  const dozens = Number(cupcakeDozens || 0);
  const units = cupcakeUnitConfigs || [];
  const configMode = cupcakeConfigMode || CUPCAKE_CONFIG_MODES.UNIFORM;
  const configModeLabel =
    CUPCAKE_CONFIG_MODE_OPTIONS.find((o) => o.id === configMode)?.title || '';
  const singleDozen = isSingleDozenOrder(dozens, units);
  const { firstHalf, secondHalf } = splitUnitsIntoHalves(units);
  const totalCupcakes = Math.round(dozens * 12);

  if (!units.length) return null;

  if (variant === 'admin') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full bg-[#a62c2b] px-3 py-1 text-xs sm:text-sm font-semibold text-white">
            {dozens} dozen · {totalCupcakes} cupcakes
          </span>
          {showConfigMode && (
            <span className="inline-flex items-center rounded-full border border-candy-primary/30 bg-red-50 px-3 py-1 text-xs sm:text-sm font-medium text-[#a62c2b]">
              {configModeLabel}
            </span>
          )}
        </div>

        {singleDozen && units[0] && (
          <AdminUnitCard
            title={configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF ? 'Half & half dozen' : 'Single dozen'}
            subtitle="12 cupcakes total"
            unit={units[0]}
          />
        )}

        {!singleDozen && configMode === CUPCAKE_CONFIG_MODES.UNIFORM && units[0] && (
          <AdminUnitCard
            title="All dozens — same setup"
            subtitle={`Applies to all ${dozens} dozen (${totalCupcakes} cupcakes)`}
            unit={units[0]}
          />
        )}

        {!singleDozen && configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF && (
          <div className="space-y-3">
            {[firstHalf, secondHalf].map((block, idx) => {
              const unit = block[0];
              if (!unit) return null;
              const blockDozens = block.reduce((sum, u) => sum + u.size, 0);
              const blockCupcakes = Math.round(blockDozens * 12);
              const title = idx === 0 ? 'First half of order' : 'Second half of order';
              return (
                <AdminUnitCard
                  key={title}
                  title={title}
                  subtitle={`${blockDozens} dozen · ${blockCupcakes} cupcakes`}
                  unit={unit}
                />
              );
            })}
          </div>
        )}

        {!singleDozen && configMode === CUPCAKE_CONFIG_MODES.BY_DOZEN && (
          <AdminByDozenTable units={units} />
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {showConfigMode && (
        <p className="text-sm text-gray-700">
          <strong>Configuration:</strong> {configModeLabel}
        </p>
      )}

      {singleDozen && units[0] && (
        <UnitBlock
          title={configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF ? 'Half and half (12 cupcakes)' : 'Your dozen (12 cupcakes)'}
          unit={units[0]}
        />
      )}

      {!singleDozen && configMode === CUPCAKE_CONFIG_MODES.UNIFORM && units[0] && (
        <>
          <p className="text-xs text-gray-600">Same setup applied to all dozens.</p>
          <UnitBlock title="All dozens" unit={units[0]} />
        </>
      )}

      {!singleDozen && configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF && (
        <>
          {[firstHalf, secondHalf].map((block, idx) => {
            const unit = block[0];
            if (!unit) return null;
            const blockDozens = block.reduce((sum, u) => sum + u.size, 0);
            const title = idx === 0 ? `First half (${blockDozens} dozen)` : `Second half (${blockDozens} dozen)`;
            return <UnitBlock key={title} title={title} unit={unit} />;
          })}
        </>
      )}

      {!singleDozen && configMode === CUPCAKE_CONFIG_MODES.BY_DOZEN &&
        units.map((unit) => (
          <UnitBlock
            key={unit.unitIndex}
            title={`${unit.label} (${unit.size === 1 ? '12 cupcakes' : '6 cupcakes'})`}
            unit={unit}
          />
        ))}
    </div>
  );
}
